import { Box } from "@mui/material";
import { FC, useCallback, useMemo, useRef, useState } from "react";
import { useIsMobile } from "../../../../hooks/useIsMobile";
import { FilterButtonDesktop } from "./FilterButtonDesktop";
import { FilterButtonMobile } from "./FilterButtonMobile";
import { FilterSelectDesktop } from "./FilterSelectDesktop";
import { FilterSelectMobile } from "./FilterSelectMobile";

export interface FilterSelectProps {
  label: string;
  options: Array<{ value: string; label: string }>;
  selectedValues: string[];
  onChange: (values: string[]) => void;
  emptySelectionMode?: "all" | "zero";
  desktopMenuMaxWidth?: number | string;
}

export const FilterSelect: FC<FilterSelectProps> = ({
  label,
  options,
  selectedValues,
  onChange,
  emptySelectionMode = "all",
  desktopMenuMaxWidth,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [tempSelectedValues, setTempSelectedValues] = useState<string[]>([]);
  const buttonWrapperRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  const handleClick = () => {
    setTempSelectedValues(selectedValues);
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleApply = () => {
    onChange(tempSelectedValues);
    setIsOpen(false);
  };

  const handleCancel = () => {
    setTempSelectedValues(selectedValues);
    setIsOpen(false);
  };

  const handleToggleOption = useCallback((optionValue: string) => {
    setTempSelectedValues((prev) =>
      prev.includes(optionValue)
        ? prev.filter((v) => v !== optionValue)
        : [...prev, optionValue],
    );
  }, []);

  const handleToggleAll = useCallback(() => {
    const allValues = options.map((option) => option.value);
    setTempSelectedValues((prev) => {
      const isAllValuesSelected =
        allValues.length > 0 &&
        allValues.every((value) => prev.includes(value));

      return isAllValuesSelected ? [] : allValues;
    });
  }, [options]);

  const isAllSelected = useMemo(
    () =>
      options.length > 0 &&
      options.every((option) => tempSelectedValues.includes(option.value)),
    [options, tempSelectedValues],
  );

  const selectedCount = selectedValues.length;
  const totalCount = options.length;

  const FilterButton = useMemo(() => {
    if (isMobile) {
      return FilterButtonMobile;
    }

    return FilterButtonDesktop;
  }, [isMobile]);

  return (
    <>
      <Box ref={buttonWrapperRef} sx={{ display: "inline-flex" }}>
        <FilterButton
          emptySelectionMode={emptySelectionMode}
          label={label}
          onClick={handleClick}
          selectedCount={selectedCount}
          totalCount={totalCount}
        />
      </Box>
      {isMobile ? (
        <FilterSelectMobile
          handleApply={handleApply}
          handleCancel={handleCancel}
          handleToggleAll={handleToggleAll}
          handleToggleOption={handleToggleOption}
          isAllSelected={isAllSelected}
          isModalOpen={isOpen}
          label={label}
          options={options}
          tempSelectedValues={tempSelectedValues}
        />
      ) : (
        <FilterSelectDesktop
          anchorEl={buttonWrapperRef.current}
          handleApply={handleApply}
          handleCancel={handleCancel}
          handleClose={handleClose}
          handleToggleAll={handleToggleAll}
          handleToggleOption={handleToggleOption}
          isAllSelected={isAllSelected}
          menuMaxWidth={desktopMenuMaxWidth}
          open={isOpen}
          options={options}
          tempSelectedValues={tempSelectedValues}
        />
      )}
    </>
  );
};
